import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  CheckCircle,
  ContentCopy,
  DirectionsCarOutlined,
  DescriptionOutlined,
  OpenInNew,
  PolicyOutlined,
  SendOutlined,
} from '@mui/icons-material';
import { caseService } from '../services/api';
import { usePortalText } from '../locales/portalTranslations';

const initialForm = {
  PolicyStartDate: '', PolicyExpiryDate: '', QuoteNumber: '',
  VehiclePlateCharacter: '', VehiclePlateNumber: '', EngineNumber: '', ChassisNumber: '',
  Make: '', Model: '', BodyType: '', ManufacturingYear: '',
  ReasonForInspection: '', PolicyCoverType: '', CoverDetails: '',
  PreviousPolicyExpiryDate: '', PreviousPolicyCoverType: '', Remarks: '',
};

const getSections = pt => [
  {
    title: pt('policyDetails'),
    description: pt('policyHelp'),
    icon: <PolicyOutlined />,
    fields: [
      ['PolicyStartDate', pt('policyStartDate'), 'date'],
      ['PolicyExpiryDate', pt('policyExpiryDate'), 'date'],
      ['QuoteNumber', pt('quoteNumber')],
    ],
  },
  {
    title: pt('vehicleDetails'),
    description: pt('vehicleHelp'),
    icon: <DirectionsCarOutlined />,
    fields: [
      ['VehiclePlateCharacter', pt('plateCharacter')], ['VehiclePlateNumber', pt('plateNumber')],
      ['EngineNumber', pt('engineNumber')], ['ChassisNumber', pt('chassisNumber')],
      ['Make', pt('make')], ['Model', pt('model')], ['BodyType', pt('bodyType')],
      ['ManufacturingYear', pt('manufacturingYear'), 'number'],
    ],
  },
  {
    title: pt('inspectionDetails'),
    description: pt('inspectionHelp'),
    icon: <DescriptionOutlined />,
    fields: [
      ['ReasonForInspection', pt('reason')], ['PolicyCoverType', pt('coverType')],
      ['CoverDetails', pt('coverDetails')], ['PreviousPolicyExpiryDate', pt('previousExpiry'), 'date'],
      ['PreviousPolicyCoverType', pt('previousCover')], ['Remarks', pt('remarks'), 'text', true],
    ],
  },
];

const validateForm = (values, sections, pt) => {
  const nextErrors = {};
  const requiredFields = sections.flatMap(section => section.fields).filter(([name]) => name !== 'Remarks');
  const fieldLabels = Object.fromEntries(sections.flatMap(section => section.fields.map(([name, label]) => [name, label])));

  requiredFields.forEach(([name]) => {
    if (!String(values[name] ?? '').trim()) {
      nextErrors[name] = pt('required', { field: fieldLabels[name] });
    }
  });

  if (values.PolicyStartDate && values.PolicyExpiryDate && values.PolicyExpiryDate < values.PolicyStartDate) {
    nextErrors.PolicyExpiryDate = pt('expiryValidation');
  }

  if (values.ManufacturingYear) {
    const year = Number(values.ManufacturingYear);
    const maximumYear = new Date().getFullYear() + 1;
    if (!Number.isInteger(year) || year < 1900 || year > maximumYear) {
      nextErrors.ManufacturingYear = pt('yearValidation', { year: maximumYear });
    }
  }

  return nextErrors;
};

const toLegacyDate = value => {
  if (!value) return value;
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

const limitMasterResults = createFilterOptions({ limit: 100 });

const CreateCasePage = ({ onCreated, drawerOpen, isMobile }) => {
  const pt = usePortalText();
  const sections = getSections(pt);
  const requiredFields = sections.flatMap(section => section.fields).filter(([name]) => name !== 'Remarks');
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(false);
  const [masters, setMasters] = useState({});
  const [mastersError, setMastersError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null);

  useEffect(() => {
    let active = true;
    caseService.getCreationMasters()
      .then(data => active && setMasters(data))
      .catch(() => active && setMastersError(pt('mastersError')));
    return () => { active = false; };
  }, []);

  const updateField = (name, value) => {
    setForm(current => {
      const nextForm = { ...current, [name]: value };
      setErrors(validateForm(nextForm, sections, pt));
      return nextForm;
    });
  };

  const touchField = name => {
    setTouched(current => ({ ...current, [name]: true }));
    setErrors(validateForm(form, sections, pt));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const normalizedForm = Object.fromEntries(
      Object.entries(form).map(([name, value]) => [name, typeof value === 'string' ? value.trim() : value])
    );
    const nextErrors = validateForm(normalizedForm, sections, pt);
    setSubmitted(true);
    setErrors(nextErrors);

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      requestAnimationFrame(() => {
        const input = document.getElementById(`create-case-${firstInvalidField}`);
        input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input?.focus({ preventScroll: true });
      });
      return;
    }

    setForm(normalizedForm);
    setIsLoading(true);
    setResult(null);
    setCopied(false);
    try {
      const response = await caseService.createFromLegacyForm({
        ...normalizedForm,
        PolicyStartDate: toLegacyDate(normalizedForm.PolicyStartDate),
        PolicyExpiryDate: toLegacyDate(normalizedForm.PolicyExpiryDate),
        PreviousPolicyExpiryDate: toLegacyDate(normalizedForm.PreviousPolicyExpiryDate),
      });
      setResult(response);
      if (response.StatusCode === 0) {
        setCustomerEmail('');
        setEmailFeedback(null);
        setForm(initialForm);
        setErrors({});
        setTouched({});
        setSubmitted(false);
        onCreated?.();
      }
    } catch (error) {
      setResult({ StatusCode: 1, Message: error.response?.data?.message || pt('unableCreate') });
    } finally {
      setIsLoading(false);
    }
  };

  const visibleError = name => (submitted || touched[name]) ? errors[name] : '';
  const invalidFieldCount = Object.keys(errors).length;
  const completedFieldCount = requiredFields.filter(([name]) => String(form[name] ?? '').trim()).length;

  const handleSendInspectionEmail = async () => {
    const email = customerEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailFeedback({ severity: 'error', message: 'Enter a valid customer email address.' });
      return;
    }

    try {
      setEmailSending(true);
      setEmailFeedback(null);
      const response = await caseService.sendInspectionEmail(result.WebReferenceNo, email);
      if (response.status !== 'success') throw new Error(response.message || 'Email could not be sent');
      window.close();
      setResult(null);
    } catch (error) {
      setEmailFeedback({ severity: 'error', message: error.response?.data?.message || error.message || 'Email could not be sent. Try again.' });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Box id="main-content" sx={{ width: '100%', minHeight: 'calc(100vh - 112px)', pb: { xs: 18, sm: 13 } }}>
      <Box sx={{ mb: { xs: 2, md: 2.5 }, textAlign: 'left' }}>
        <Typography variant="caption" color="text.secondary">{pt('casesCreateCase')}</Typography>
        <Typography component="h1" variant="h1" sx={{ mt: 0.25, fontSize: { xs: '1.5rem', md: '1.875rem' }, lineHeight: 1.25 }}>
          {pt('createCase')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {pt('createCaseIntro')}
        </Typography>
      </Box>

      {submitted && invalidFieldCount > 0 && (
        <Alert severity="warning" role="alert" sx={{ mb: 2 }}>
          {pt('reviewFields', { count: invalidFieldCount })}
        </Alert>
      )}

      {result && result.StatusCode !== 0 && (
        <Alert severity="error" role="alert" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>{result.Message}</Typography>
          {result.validation?.ErrorList?.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {result.validation.ErrorList.map(error => <li key={error}>{error}</li>)}
            </Box>
          )}
        </Alert>
      )}

      <Dialog
        open={result?.StatusCode === 0}
        onClose={() => setResult(null)}
        aria-labelledby="case-created-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 5 }, pt: 5, pb: 2 }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 1.5 }} />
          <Typography id="case-created-title" variant="h2">{pt('created')}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {pt('createdHelp')}
          </Typography>

          <Alert severity="info" icon={false} sx={{ mt: 2.5, textAlign: 'left' }}>
            <Typography variant="body2"><strong>{pt('customerInspect')}</strong> {pt('customerInspectHelp')}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}><strong>{pt('staffInspect')}</strong> {pt('staffInspectHelp')}</Typography>
          </Alert>

          <Paper variant="outlined" sx={{ mt: 3, p: 2.5, textAlign: 'left', bgcolor: 'background.default' }}>
            <Typography variant="caption" color="text.secondary">{pt('reference')}</Typography>
            <Typography variant="h4" sx={{ mt: 0.5 }}>{result?.WebReferenceNo}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">{pt('customerLink')}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{result?.WebBrowserLink}</Typography>
            <Button
              size="small"
              startIcon={<ContentCopy />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(result.WebBrowserLink);
                  setCopied(true);
                } catch {
                  window.prompt('Copy the inspection link:', result.WebBrowserLink);
                }
              }}
              sx={{ mt: 1.5 }}
            >
              {copied ? pt('linkCopied') : pt('copyLink')}
            </Button>
          </Paper>

          <Box sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="h5">{pt('sendCustomer')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              {pt('sendCustomerHelp')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                type="email"
                label={pt('customerEmail')}
                placeholder="customer@example.com"
                value={customerEmail}
                onChange={event => {
                  setCustomerEmail(event.target.value);
                  setEmailFeedback(null);
                }}
                onKeyDown={event => event.key === 'Enter' && handleSendInspectionEmail()}
                autoComplete="email"
              />
              <Button
                variant="contained"
                onClick={handleSendInspectionEmail}
                disabled={emailSending || emailFeedback?.severity === 'success'}
                startIcon={emailSending ? <CircularProgress size={18} color="inherit" /> : <SendOutlined />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {emailSending ? pt('sending') : emailFeedback?.severity === 'success' ? pt('sent') : pt('sendLink')}
              </Button>
            </Box>
            {emailFeedback && <Alert severity={emailFeedback.severity} sx={{ mt: 1.5 }}>{emailFeedback.message}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, px: 3, pb: 4 }}>
          <Button variant="outlined" onClick={() => setResult(null)}>{pt('anotherCase')}</Button>
          <Button variant="contained" href={result?.WebBrowserLink} target="_blank" endIcon={<OpenInNew />}>
            {pt('openInspection')}
          </Button>
        </DialogActions>
      </Dialog>

      {mastersError && <Alert severity="error" sx={{ mb: 3 }}>{mastersError}</Alert>}

      <Paper
        component="form"
        noValidate
        onSubmit={handleSubmit}
        aria-busy={isLoading}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 8px 28px rgba(15, 23, 42, 0.05)' }}
      >
        {sections.map((section, sectionIndex) => (
          <Box key={section.title}>
            <Box sx={{ p: { xs: 2, sm: 3, lg: 3.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, textAlign: 'left' }}>
                <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', bgcolor: 'rgba(21,101,192,0.08)', color: 'primary.main', borderRadius: 1, flexShrink: 0 }}>
                  {section.icon}
                </Box>
                <Box>
                  <Typography variant="h4">{section.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{section.description}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                {section.fields.map(([name, label, type = 'text', multiline = false]) => {
                  const options = masters[name];
                  const error = visibleError(name);
                  return options ? (
                    <Autocomplete
                      key={name}
                      options={options}
                      value={form[name] || null}
                      filterOptions={limitMasterResults}
                      onChange={(_, value) => updateField(name, value || '')}
                      onBlur={() => touchField(name)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          id={`create-case-${name}`}
                          required
                          label={label}
                          name={name}
                          error={Boolean(error)}
                          helperText={error || ' '}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      key={name}
                      id={`create-case-${name}`}
                      fullWidth
                      required={name !== 'Remarks'}
                      type={type}
                      name={name}
                      label={label}
                      value={form[name]}
                      multiline={multiline}
                      minRows={multiline ? 3 : undefined}
                      onChange={event => updateField(name, event.target.value)}
                      onBlur={() => touchField(name)}
                      error={Boolean(error)}
                      helperText={error || ' '}
                      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                      inputProps={name === 'ManufacturingYear'
                        ? { min: 1900, max: new Date().getFullYear() + 1, inputMode: 'numeric' }
                        : undefined}
                    />
                  );
                })}
              </Box>
            </Box>
            {sectionIndex < sections.length - 1 && <Divider />}
          </Box>
        ))}

        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: isMobile ? 0 : drawerOpen ? 284 : 24,
            right: isMobile ? 0 : 24,
            zIndex: 1100,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2, sm: 2.5 },
            pt: 2,
            pb: 'max(16px, env(safe-area-inset-bottom))',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(8px)',
            borderTop: 1,
            borderColor: 'divider',
            boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.07)',
            transition: 'left 0.2s ease',
          }}
        >
          <Box aria-live="polite">
            <Typography variant="body2" fontWeight={600} color={submitted && invalidFieldCount ? 'error.main' : 'text.primary'}>
              {submitted && invalidFieldCount
                ? pt('attentionFields', { count: invalidFieldCount })
                : pt('completedFields', { current: completedFieldCount, total: requiredFields.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary">{pt('requiredNote')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, width: { xs: '100%', sm: 'auto' }, '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
            <Button
              type="button"
              variant="outlined"
              disabled={isLoading}
              onClick={() => {
                setForm(initialForm);
                setResult(null);
                setErrors({});
                setTouched({});
                setSubmitted(false);
              }}
            >
              {pt('clearForm')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <AddCircleOutline fontSize="small" />}
              sx={{ minWidth: 150 }}
            >
              {isLoading ? pt('creating') : pt('createCase')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateCasePage;
